import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useZakeke } from '@zakeke/zakeke-configurator-react';
import { Dialog, DialogWindow } from '../dialogs/Dialogs';
import { ReactComponent as CameraIcon } from '../../assets/svg/camera-solid.svg';

interface WebcamCaptureDialogProps {
	onCapture: (file: File) => void;
	onClose: () => void;
}

export const WebcamCaptureDialog: React.FC<WebcamCaptureDialogProps> = ({ onCapture, onClose }) => {
	const { T_static } = useZakeke();
	const T = useMemo(() => ({ _: T_static }), [T_static]);
	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const streamRef = useRef<MediaStream | null>(null);

	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isCaptured, setIsCaptured] = useState(false);

	// Initialize webcam stream
	useEffect(() => {
		const abortController = new AbortController();

		const initWebcam = async () => {
			try {
				if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
					setError(T._('Camera not supported in this browser'));
					setIsLoading(false);
					return;
				}

				const stream = await navigator.mediaDevices.getUserMedia({
					video: true,
					audio: false,
				});

				if (abortController.signal.aborted) {
					stream.getTracks().forEach((track) => track.stop());
					return;
				}

				streamRef.current = stream;

				if (videoRef.current) {
					videoRef.current.srcObject = stream;
				}

				setIsLoading(false);
			} catch (err) {
				if (abortController.signal.aborted) return;

				console.error('Webcam initialization error:', err);

				if (err instanceof Error) {
					if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
						setError(T._('Camera access denied. Please allow camera access and try again.'));
					} else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
						setError(T._('No camera found. Please connect a camera and try again.'));
					} else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
						setError(T._('Camera is already in use by another application.'));
					} else {
						setError(T._('Failed to access camera. Please check your camera settings.'));
					}
				} else {
					setError(T._('An unexpected error occurred while accessing the camera.'));
				}

				setIsLoading(false);
			}
		};

		initWebcam();

		return () => {
			abortController.abort();
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
			}
		};
	}, [T]);

	// Capture photo from video stream
	const handleCapture = useCallback(() => {
		if (!videoRef.current || !canvasRef.current) return;

		const video = videoRef.current;
		const canvas = canvasRef.current;
		const context = canvas.getContext('2d');

		if (!context) return;

		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;
		context.drawImage(video, 0, 0, canvas.width, canvas.height);

		try {
			canvas.toBlob(
				(blob) => {
					if (!blob) {
						console.error('Failed to create blob from canvas');
						return;
					}

					const timestamp = new Date().getTime();
					const file = new File([blob], `webcam-capture-${timestamp}.jpg`, {
						type: 'image/jpeg',
						lastModified: timestamp,
					});

					onCapture(file);
					setIsCaptured(true);

					if (streamRef.current) {
						streamRef.current.getTracks().forEach((track) => track.stop());
					}

					setTimeout(() => {
						onClose();
					}, 300);
				},
				'image/jpeg',
				0.9
			);
		} catch (error) {
			console.error(error);
			onClose();
		}
	}, [onCapture, onClose]);

	// Handle close with cleanup
	const handleClose = useCallback(() => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
		}
		onClose();
	}, [onClose]);

	return (
		<Dialog
			windowDecorator={StyledDialogWindow}
			showCloseButton={!isLoading}
			onClose={handleClose}
		>
			<ContentWrapper>
				{isLoading && (
					<StatusMessage>
						<CameraIcon />
						<p>{T._('Initializing camera...')}</p>
					</StatusMessage>
				)}

				{error && (
					<StatusMessage>
						<ErrorText>{error}</ErrorText>
					</StatusMessage>
				)}
				<VideoContainer $captured={isCaptured} $hidden={!!(isLoading || error)}>
					<Video ref={videoRef} autoPlay playsInline muted />
					<Canvas ref={canvasRef} style={{ display: 'none' }} />
				</VideoContainer>
				{!isLoading && !error && (
					<ButtonsContainer>
						<CancelButton onClick={handleClose} disabled={isCaptured}>
							{T._('Cancel')}
						</CancelButton>
						<CaptureButton onClick={handleCapture} disabled={isCaptured}>
							{T._('Capture Photo')}
						</CaptureButton>
					</ButtonsContainer>
				)}
			</ContentWrapper>
		</Dialog>
	);
};

// Styled Components

const StyledDialogWindow = styled(DialogWindow)`
	max-width: 720px;
	width: 90vw;
	padding: 1.5rem;
`;

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	align-items: center;
`;

const VideoContainer = styled.div<{ $captured: boolean; $hidden: boolean }>`
	max-width: 640px;
	height: calc(100dvh - 400px);
	aspect-ratio: 16 / 9;
	border-radius: 0.75rem;
	overflow: hidden;
	background-color: #000;
	position: relative;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
	transition: all 0.3s;
	display: ${(props) => (props.$hidden ? 'none' : 'block')};

	${(props) =>
		props.$captured &&
		`
		opacity: 0.7;
		transform: scale(0.98);
	`}
`;

const Video = styled.video`
	width: 100%;
	height: 100%;
	object-fit: contain;
	display: block;
`;

const Canvas = styled.canvas`
	display: none;
`;

const ButtonsContainer = styled.div`
	display: flex;
	gap: 1rem;
	justify-content: center;
	width: 100%;
`;

const CancelButton = styled.button`
	padding: 0.5rem 1.5rem;
	border: 1px solid #d1d5db;
	border-radius: 0.5rem;
	background: white;
	color: #374151;
	font-size: 0.875rem;
	cursor: pointer;
	transition: all 0.2s;

	&:hover:not(:disabled) {
		background: #f3f4f6;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const CaptureButton = styled.button`
	padding: 0.5rem 1.5rem;
	border: none;
	border-radius: 0.5rem;
	background: linear-gradient(90deg, #a855f7, #6366f1);
	color: white;
	font-size: 0.875rem;
	cursor: pointer;
	transition: all 0.2s;

	&:hover:not(:disabled) {
		opacity: 0.9;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const StatusMessage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1rem;
	padding: 3rem 1.5rem;
	color: #6b7280;

	svg {
		width: 64px;
		height: 64px;
		opacity: 0.5;
	}

	p {
		font-size: 1rem;
		margin: 0;
	}
`;

const ErrorText = styled.p`
	color: #ef4444;
	text-align: center;
	margin: 0;
	font-size: 1rem;
`;
